import { useState } from "react";
import { useLocation } from "react-router";
import { styled } from "styled-components";

import { ExpandableContent, PlayIcon } from "common/components";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useGo } from "common/utilities/useGo";
import { useTranslate } from "common/utilities/useTranslate";
import type { CustomKnowledge } from "domains/customContentAndSources/types";
import { DataItem } from "domains/knowledgeData/components/DataItem";
import { PublicationCardBody } from "domains/knowledgeFeed/components/PublicationCardBody";
import { PublicationCardHead } from "domains/knowledgeFeed/components/PublicationCardHead";
import { VideoCard } from "domains/knowledgeFeed/components/VideoCard";
import { interpolate } from "domains/language/interpolate";
import { KnowledgeSignalSection } from "domains/signals/components/KnowledgeSignalSection";
import { useMedia } from "domains/viewport/useMedia";
import { setVisualMediaPlayer } from "domains/visualMediaPlayer/actions";
import { MediaType } from "domains/visualMediaPlayer/types";
import { getKnowledgeType } from "entities/knowledge/reducers";
import type { Knowledge } from "entities/knowledge/types";
import { KnowledgeType } from "entities/knowledge/types";
import { getCanAccessTopicSignals } from "entities/permissions/selectors";

const StyledSmallPlayIcon = styled(PlayIcon)`
  transition: transform 0.3s;
  width: 42px;
  height: 42px;
  color: white;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 1rem;
  margin: auto;
`;

const StyledListLink = styled.div`
  display: flex;
  flex-direction: column;

  &:hover ${StyledSmallPlayIcon} {
    transform: scale(1.2);
  }
`;

const StyledBanner = styled.div`
  color: ${({ theme }) => theme.colors.fillTertiary};
  margin-bottom: 0.4rem;
  padding: 0.2rem 0 0.4rem 0;
  font-size: 0.65rem;
`;

type LatestKnowledgeFeedItemProps = { className?: string; knowledge: Knowledge | CustomKnowledge };

export const LatestKnowledgeFeedItem = ({ knowledge }: LatestKnowledgeFeedItemProps) => {
  const dispatch = useDispatch();
  const go = useGo();
  const location = useLocation();
  const isSmallScreen = useMedia({ small: true, medium: true }, false);
  const hasAccessToSignals = useSelector(getCanAccessTopicSignals);
  const [isExpanded, setIsExpanded] = useState(false);
  const translate = useTranslate();

  const toggleExpansion = () => setIsExpanded(!isExpanded);

  const { signals } = knowledge;
  const showSignalContent = hasAccessToSignals && signals && signals.length > 0;

  const updateVisualMediaPlayer = () => {
    if (isSmallScreen) {
      go(`${location.pathname}/videos/${knowledge.id}`);
      return;
    }
    let url = knowledge.embedded_media?.url;
    if (!url) {
      if ("attachment" in knowledge && knowledge.attachment?.url) {
        url = knowledge.attachment.url;
      } else if ("media" in knowledge && knowledge.media?.url) {
        url = knowledge.media.url;
      }
    }
    dispatch(
      setVisualMediaPlayer({
        id: knowledge.id,
        mediaType: !knowledge.embedded_media?.url
          ? MediaType.videoViaDirectFileLink
          : MediaType.videoViaEmbeddedHtmlPlayer,
        name: knowledge.title,
        url,
      }),
    );
  };

  const articleAnnouncement = (
    <>
      {showSignalContent && (
        <StyledBanner data-test-id="article-has-relevant-signal-announcement-label">
          {interpolate(translate("app.article.article_announcement.transformational"), {
            adjective: (adjective: string) => <strong>{adjective}</strong>,
          })}
        </StyledBanner>
      )}
    </>
  );

  const getLink = (path: string) => `${path}/${knowledge.id}`;

  switch (getKnowledgeType(knowledge)) {
    case KnowledgeType.publication: {
      const path = `${location.pathname}/publications`;
      return (
        <StyledListLink onClick={toggleExpansion}>
          {articleAnnouncement}
          <PublicationCardHead knowledge={knowledge} link={getLink(path)} />
          <ExpandableContent isActive={isExpanded}>
            <PublicationCardBody knowledge={knowledge} link={getLink(path)} />
            <KnowledgeSignalSection signals={signals} />
          </ExpandableContent>
        </StyledListLink>
      );
    }
    case KnowledgeType.video: {
      const path = `${location.pathname}/videos`;
      return (
        <StyledListLink onClick={updateVisualMediaPlayer}>
          {articleAnnouncement}
          <VideoCard knowledge={knowledge} link={getLink(path)} />
        </StyledListLink>
      );
    }
    case KnowledgeType.data: {
      const path = `${location.pathname}/interactive-data`;
      return (
        <DataItem articleAnnouncement={articleAnnouncement} knowledge={knowledge} latestView link={getLink(path)} />
      );
    }
    default:
      return null;
  }
};
